import { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FilterContext, FilterDispatchContext } from "../../Context/FilterContext";

const SALE_PRESETS = [
  { label: "Under 5M", min: "", max: "5000000" },
  { label: "5-10M", min: "5000000", max: "10000000" },
  { label: "10-25M", min: "10000000", max: "25000000" },
  { label: "25-50M", min: "25000000", max: "50000000" },
  { label: "50-100M", min: "50000000", max: "100000000" },
  { label: "100M+", min: "100000000", max: "" },
];

const LEASE_PRESETS = [
  { label: "Under 25K", min: "", max: "25000" },
  { label: "25-50K", min: "25000", max: "50000" },
  { label: "50-100K", min: "50000", max: "100000" },
  { label: "100-200K", min: "100000", max: "200000" },
  { label: "200K+", min: "200000", max: "" },
];

const Wrapper = styled.div`
  position: relative;

  .price-trigger {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 14px;
    border: none;
    border-radius: 8px;
    background: #fff;
    font-size: 14px;
    color: var(--primary-grey-500);
    cursor: pointer;
    box-shadow: var(--elevation-1dp);
    transition: all 150ms ease;
    white-space: nowrap;

    &:hover, &.open {
      box-shadow: var(--elevation-2dp);
    }

    &.active {
      background: var(--primary-100);
    }
  }

  .price-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 100;
    width: 320px;
    background: #fff;
    border-radius: 8px;
    box-shadow: var(--elevation-2dp);
    padding: 16px;

    .price-inputs-row {
      display: flex;
      gap: 8px;
      align-items: center;
      margin-bottom: 12px;

      .price-field {
        flex: 1;
        position: relative;

        label {
          display: block;
          font-size: 11px;
          color: var(--primary-grey-300);
          margin-bottom: 4px;
        }

        input {
          width: 100%;
          padding: 8px 10px;
          border: 1px solid var(--primary-grey-200);
          border-radius: 6px;
          font-size: 14px;
          outline: none;
          transition: border-color 150ms;

          &:focus {
            border-color: var(--color-primary);
          }

          &::placeholder {
            color: var(--color-secondary-200);
          }
        }
      }

      .price-separator {
        padding-top: 18px;
        color: var(--primary-grey-200);
      }
    }

    .price-presets {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;

      button {
        padding: 6px 12px;
        border: 1px solid var(--primary-grey-200);
        border-radius: 100px;
        background: #fff;
        font-size: 12px;
        color: var(--primary-grey-500);
        cursor: pointer;
        transition: all 150ms;

        &:hover {
          border-color: var(--color-secondary-300);
          background: var(--background-grey-100);
        }

        &.active {
          background: var(--primary-100);
          border-color: var(--color-primary);
          color: var(--color-secondary-600);
          font-weight: 600;
        }
      }
    }

    .price-actions {
      display: flex;
      justify-content: flex-end;
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid var(--background-grey-200);
      gap: 8px;

      button {
        padding: 8px 16px;
        border-radius: 100px;
        font-size: 13px;
        cursor: pointer;
        transition: all 150ms;
      }

      .price-clear {
        background: transparent;
        border: none;
        color: var(--color-secondary);

        &:hover {
          text-decoration: underline;
        }
      }

      .price-apply {
        background: linear-gradient(135deg, var(--color-primary), #C4922E);
        color: #fff;
        border: none;
        font-weight: 700;

        &:hover {
          opacity: 0.9;
        }
      }
    }
  }
`;

const formatDisplay = (value) => {
  const num = parseInt(value, 10);
  if (!num) return "";
  if (num >= 1000000) return `${(num / 1000000).toFixed(num % 1000000 === 0 ? 0 : 1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
  return num.toLocaleString();
};

const formatInput = (value) => {
  const num = parseInt(String(value).replace(/,/g, ""), 10);
  if (!num) return "";
  return num.toLocaleString("en-US");
};

const parseInput = (value) => {
  return String(value).replace(/,/g, "");
};

const PriceInputs = () => {
  const filters = useContext(FilterContext) || {};
  const dispatch = useContext(FilterDispatchContext);
  const transactionType = filters.transactionType || "sale";

  const [open, setOpen] = useState(false);
  const [minVal, setMinVal] = useState(filters.min_price || "");
  const [maxVal, setMaxVal] = useState(filters.max_price || "");
  const wrapperRef = useRef(null);

  const presets = transactionType === "lease" ? LEASE_PRESETS : SALE_PRESETS;

  useEffect(() => {
    setMinVal(filters.min_price || "");
    setMaxVal(filters.max_price || "");
  }, [filters.min_price, filters.max_price]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const applyPrice = (min, max) => {
    if (min) {
      dispatch({ action: "set", filter: { key: "min_price", value: min } });
    } else {
      dispatch({ action: "delete", filter: { key: "min_price" } });
    }
    if (max) {
      dispatch({ action: "set", filter: { key: "max_price", value: max } });
    } else {
      dispatch({ action: "delete", filter: { key: "max_price" } });
    }
  };

  const handleApply = () => {
    applyPrice(parseInput(minVal), parseInput(maxVal));
    setOpen(false);
  };

  const handleClear = () => {
    setMinVal("");
    setMaxVal("");
    applyPrice("", "");
    setOpen(false);
  };

  const handlePreset = (preset) => {
    setMinVal(preset.min);
    setMaxVal(preset.max);
    applyPrice(preset.min, preset.max);
    setOpen(false);
  };

  const isPresetActive = (preset) => {
    return String(filters.min_price || "") === preset.min &&
           String(filters.max_price || "") === preset.max;
  };

  const hasPrice = filters.min_price || filters.max_price;
  let triggerLabel = "Price";
  if (hasPrice) {
    const parts = [];
    if (filters.min_price) parts.push(`${formatDisplay(filters.min_price)}`);
    if (filters.max_price) parts.push(`${formatDisplay(filters.max_price)}`);
    triggerLabel = parts.length === 2
      ? `\u20B1 ${parts[0]} - ${parts[1]}`
      : filters.min_price
        ? `\u20B1 ${parts[0]}+`
        : `Up to \u20B1 ${parts[0]}`;
  }

  return (
    <Wrapper ref={wrapperRef}>
      <button
        type="button"
        className={`price-trigger${open ? " open" : ""}${hasPrice ? " active" : ""}`}
        onClick={() => setOpen(!open)}
      >
        {triggerLabel}
        <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
          <path d="M2 4l3 3 3-3"/>
        </svg>
      </button>

      {open && (
        <div className="price-dropdown">
          <div className="price-inputs-row">
            <div className="price-field">
              <label>Minimum</label>
              <input
                type="text"
                placeholder="No min"
                value={formatInput(minVal)}
                onChange={(e) => setMinVal(parseInput(e.target.value))}
              />
            </div>
            <span className="price-separator">&mdash;</span>
            <div className="price-field">
              <label>Maximum</label>
              <input
                type="text"
                placeholder="No max"
                value={formatInput(maxVal)}
                onChange={(e) => setMaxVal(parseInput(e.target.value))}
              />
            </div>
          </div>

          <div className="price-presets">
            {presets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                className={isPresetActive(preset) ? "active" : ""}
                onClick={() => handlePreset(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="price-actions">
            <button type="button" className="price-clear" onClick={handleClear}>Clear</button>
            <button type="button" className="price-apply" onClick={handleApply}>Apply</button>
          </div>
        </div>
      )}
    </Wrapper>
  );
};

export default PriceInputs;
